import type { TFunction } from "i18next";

import { z } from "zod";

const songListItemSchema = z.object({
  workcode: z.string().trim().min(1, "Please select a valid work."),
  title: z.string(),
  performingartists: z.string().optional(),
  label: z.string().optional(),
});

function createCoverSongSchema(t: TFunction) {
  return z.object({
    artist: z.string().optional(),
    song: z.string().optional(),
    tunecode: z.string().optional(),
  }).superRefine((item, ctx) => {
    // replaces check Invalid cover util func
    const artistLength = item.artist?.trim().length ?? 0;
    const songLength = item.song?.trim().length ?? 0;
    const tuneCodeLength = item.tunecode?.trim().length ?? 0;
    const validTuneCode = item.tunecode?.trim().length === 8;

    if (artistLength > 0 && !(songLength > 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("regex:cover-song-song-title-error", "If you enter Artist Name, then you should also enter Song Title."),
        path: ["song"],
      });
    }
    if (songLength > 0 && !(artistLength > 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("regex:cover-song-artist-name-error", "If you enter Song Title, then you should also enter Artist Name."),
        path: ["artist"],
      });
    }
    if (tuneCodeLength > 0 && !validTuneCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("regex:cover-song-tunecode-character-error", "If you enter Tune Code, it should exactly have 8 characters."),
        path: ["tunecode"],
      });
    }
    if (tuneCodeLength > 0 && validTuneCode && !(artistLength > 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("regex:cover-song-tunecode-both-error", "If you enter Tune Code, you should enter both Artist Name and Song Title."),
        path: ["artist"],
      });
    }
    if (tuneCodeLength > 0 && validTuneCode && !(songLength > 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("regex:cover-song-tunecode-both-error", "If you enter Tune Code, you should enter both Artist Name and Song Title."),
        path: ["song"],
      });
    }
  });
}

export function createSetlistSchema(t: TFunction) {
  return z.object({
    name: z.string().min(1, t("regex:unnamed-setlist-error", "Please give your setlist a name.")),
    notes: z.string().optional(),
    songList: z.array(songListItemSchema).min(1, t("regex:at-least-one-song-error", "Please add at least 1 song.")),
    coverSongs: z.array(createCoverSongSchema(t)).optional(),
  });
}

export type SetlistFormData = z.infer<ReturnType<typeof createSetlistSchema>>;
